import { useState } from "react";
import {
  Archive,
  CheckCircle2,
  FolderOpen,
  Mail,
  MessageSquarePlus,
  MoreVertical,
  Pencil,
  XCircle,
} from "lucide-react";
import {
  Caption,
  VButton,
  VCheckbox,
  VDialog,
  VDialogContent,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuTrigger,
  VField,
  VTextarea,
} from "@/components/ui";
import type { ReservationRequest } from "@/v2/shared/model";
import { fullName } from "@/v2/shared/format";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useAddComment, useArchiveRequest, useCloseRequest } from "@/v2/data/mutations";
import { AcceptDialog } from "@/v2/aanvragen/dossierDialogs";
import { EditRequestDialog } from "@/v2/aanvragen/editRequestDialog";

type DialogKind = "ask" | "accept" | "edit" | "close" | "archive" | null;

// Actiebalk op het detail (beheerder): enkel "Aanvaarden" staat los, de rest
// (incl. de link naar het dossier) zit achter het 3-puntjesmenu. Terugkoppeling
// naar de aanvrager verloopt via een opmerking die gemaild wordt (CB-10); er is
// geen status "wacht op antwoord".
export function DetailActions({
  request,
  onArchived,
  onCommentPlaced,
  onGoToDossier,
}: {
  request: ReservationRequest;
  onArchived: () => void;
  onCommentPlaced?: () => void;
  onGoToDossier?: () => void;
}): React.JSX.Element | null {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const isManager = user.role === "manager";

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [message, setMessage] = useState("");
  const [sendMail, setSendMail] = useState(false);
  const [error, setError] = useState("");

  const addComment = useAddComment();
  const closeRequest = useCloseRequest();
  const archive = useArchiveRequest();

  function closeDialog(): void {
    setDialog(null);
    setMessage("");
    setSendMail(false);
    setError("");
  }

  // Enkel "Aanvaarden" is een losse actie; de rest zit achter het
  // 3-puntjesmenu. Bij een afgesloten aanvraag blijft enkel de link naar het
  // dossier over.
  const canAct = request.status !== "CLOSED";
  const managerActions =
    isManager && (canAct || onGoToDossier) ? (
      <>
        {request.status === "OPEN" ? (
          <VButton onClick={() => setDialog("accept")}>
            <CheckCircle2 className="size-4" aria-hidden />
            Aanvaarden
          </VButton>
        ) : null}
        {canAct ? (
          <VButton variant="outline" onClick={() => setDialog("ask")}>
            <MessageSquarePlus className="size-4" aria-hidden />
            Opmerking plaatsen
          </VButton>
        ) : null}
        <VDropdownMenu>
          <VDropdownMenuTrigger asChild>
            <VButton variant="outline" size="icon" aria-label="Meer acties">
              <MoreVertical className="size-4" aria-hidden />
            </VButton>
          </VDropdownMenuTrigger>
          <VDropdownMenuContent align="end" className="min-w-[12rem]">
            {onGoToDossier ? (
              <VDropdownMenuItem className="gap-2" onSelect={onGoToDossier}>
                <FolderOpen className="size-4" aria-hidden />
                Naar dossier
              </VDropdownMenuItem>
            ) : null}
            {canAct ? (
              <>
                <VDropdownMenuItem className="gap-2" onSelect={() => setDialog("edit")}>
                  <Pencil className="size-4" aria-hidden />
                  Bewerken
                </VDropdownMenuItem>
                <VDropdownMenuItem className="gap-2" onSelect={() => setDialog("close")}>
                  <XCircle className="size-4" aria-hidden />
                  Afsluiten
                </VDropdownMenuItem>
                <VDropdownMenuItem className="gap-2" onSelect={() => setDialog("archive")}>
                  <Archive className="size-4" aria-hidden />
                  Verwijderen
                </VDropdownMenuItem>
              </>
            ) : null}
          </VDropdownMenuContent>
        </VDropdownMenu>
      </>
    ) : null;

  if (!managerActions) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {managerActions}

      {/* CB-10/CB-15 — opmerking plaatsen: interne notitie, geplakte
          binnengekomen mail of terugkoppeling. Mailen naar de aanvrager is
          een bewuste keuze via het vinkje. */}
      <VDialog open={dialog === "ask"} onOpenChange={(o) => (o ? setDialog("ask") : closeDialog())}>
        <VDialogContent
          title="Opmerking plaatsen"
          description="Plaats een opmerking bij de aanvraag: een interne notitie, een binnengekomen mail of een verduidelijkingsvraag voor de aanvrager."
        >
          <div className="space-y-4">
            <VField label="Opmerking" htmlFor="ask-message" required error={error}>
              <VTextarea
                id="ask-message"
                rows={4}
                placeholder="Bv. kan je het plannetje met de exacte kijkrichting toevoegen? Of plak hier een binnengekomen mail."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError("");
                }}
              />
            </VField>
            <VCheckbox
              checked={sendMail}
              onCheckedChange={(c) => setSendMail(c === true)}
              label="Verstuur deze opmerking ook als mail naar de aanvrager"
            />
            {sendMail ? (
              <div className="flex items-center gap-2 rounded-lg bg-info-50 px-3 py-2 text-info-800">
                <Mail className="size-4 shrink-0" aria-hidden />
                <Caption tone="default" className="text-info-800">
                  De aanvrager ontvangt deze opmerking per mail.
                </Caption>
              </div>
            ) : null}
            <DialogFooter
              confirmLabel="Plaatsen"
              pending={addComment.isPending}
              onCancel={closeDialog}
              onConfirm={() => {
                if (!message.trim()) {
                  setError("Schrijf een opmerking.");
                  return;
                }
                addComment.mutate(
                  { id: request.id, text: message, author, sendMail, handlerId: user.id },
                  {
                    onSuccess: () => {
                      closeDialog();
                      onCommentPlaced?.();
                    },
                  },
                );
              }}
            />
          </div>
        </VDialogContent>
      </VDialog>

      {/* CB-12 — aanvaarden: dossier aanmaken, meteen inplannen of wachtrij */}
      <AcceptDialog
        open={dialog === "accept"}
        onOpenChange={(o) => (o ? setDialog("accept") : closeDialog())}
        requestId={request.id}
        defaultStart={request.startDate}
        defaultEnd={request.endDate}
      />

      {/* CB-1 — aanvraag corrigeren */}
      <EditRequestDialog
        open={dialog === "edit"}
        onOpenChange={(o) => (o ? setDialog("edit") : closeDialog())}
        request={request}
      />

      {/* CB-37 — afsluiten */}
      <VDialog open={dialog === "close"} onOpenChange={(o) => (o ? setDialog("close") : closeDialog())}>
        <VDialogContent
          title="Aanvraag afsluiten"
          description="De aanvraag krijgt de status “Afgesloten”. Onderliggende dossiers sluiten mee af. Dit kan ook zonder dat er een inzet is gebeurd."
          size="sm"
        >
          <DialogFooter
            confirmLabel="Afsluiten"
            pending={closeRequest.isPending}
            onCancel={closeDialog}
            onConfirm={() =>
              closeRequest.mutate({ id: request.id, author }, { onSuccess: closeDialog })
            }
          />
        </VDialogContent>
      </VDialog>

      {/* CB-38 — soft delete */}
      <VDialog open={dialog === "archive"} onOpenChange={(o) => (o ? setDialog("archive") : closeDialog())}>
        <VDialogContent
          title="Aanvraag verwijderen"
          description="De aanvraag wordt gearchiveerd (soft delete) en verdwijnt uit het overzicht. Ze wordt niet definitief gewist."
          size="sm"
        >
          <DialogFooter
            confirmLabel="Verwijderen"
            destructive
            pending={archive.isPending}
            onCancel={closeDialog}
            onConfirm={() =>
              archive.mutate(
                { id: request.id, author },
                {
                  onSuccess: () => {
                    closeDialog();
                    onArchived();
                  },
                },
              )
            }
          />
        </VDialogContent>
      </VDialog>
    </div>
  );
}

export function DialogFooter({
  confirmLabel,
  pending,
  destructive,
  onCancel,
  onConfirm,
}: {
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
      <VButton variant="ghost" onClick={onCancel}>
        Annuleren
      </VButton>
      <VButton
        variant={destructive ? "destructive" : "primary"}
        disabled={pending}
        onClick={onConfirm}
      >
        {confirmLabel}
      </VButton>
    </div>
  );
}
